import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle, ExternalLink, X, Shield, RefreshCw, Copy, Check, Globe, Link2 } from 'lucide-react';

/**
 * URLViewerModal - Secure in-app viewer for external URLs
 * 
 * Security features:
 * - Sandboxed iframe to prevent XSS and malicious scripts
 * - URL validation to block javascript:, data:, file: schemes
 * - Security warnings for users
 * - Copy link functionality for safe external viewing
 * - Auto-fallback to "Open in New Tab" when iframe fails
 */
const URLViewerModal = ({ isOpen, onClose, url }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [iframeBlocked, setIframeBlocked] = useState(false);
  const [showIframe, setShowIframe] = useState(false);

  // Reset state when URL changes
  useEffect(() => {
    if (isOpen && url) {
      setIsLoading(true);
      setLoadError(false);
      setCopied(false);
      setIframeBlocked(false);
      setShowIframe(false);
      
      // Start a timeout - if iframe doesn't load in 3 seconds, show fallback
      const timeout = setTimeout(() => {
        if (isLoading) {
          setIframeBlocked(true);
          setIsLoading(false);
        }
      }, 3000);
      
      return () => clearTimeout(timeout);
    }
  }, [isOpen, url]);

  // Validate URL - only allow http and https
  const isValidUrl = (urlString) => {
    if (!urlString) return false;
    try {
      const urlObj = new URL(urlString);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Get display hostname from URL
  const getHostname = (urlString) => {
    try {
      const urlObj = new URL(urlString);
      return urlObj.hostname;
    } catch {
      return 'Unknown';
    }
  };

  // Copy URL to clipboard
  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  // Open in new tab (with security attributes)
  const handleOpenExternal = useCallback(() => {
    if (isValidUrl(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [url]);

  const handleIframeLoad = () => {
    setIsLoading(false);
    setShowIframe(true);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setLoadError(true);
    setIframeBlocked(true);
  };

  // Try to load in iframe
  const handleTryIframe = () => {
    setIframeBlocked(false);
    setIsLoading(true);
    setShowIframe(true);
  };

  if (!isOpen) return null;

  const validUrl = isValidUrl(url);
  const hostname = getHostname(url);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl w-[95vw] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="px-4 py-3 border-b bg-gray-50 dark:bg-gray-800 rounded-t-lg">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Link2 className="w-4 h-4" />
              External Link
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        {/* Content Area */}
        <div className="p-6 bg-white dark:bg-gray-900">
          {!validUrl ? (
            /* Invalid URL Warning */
            <div className="text-center py-8">
              <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-2">
                Blocked: Invalid URL
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                This URL was blocked for security reasons. Only HTTP and HTTPS links are allowed.
              </p>
              <p className="text-sm text-gray-500 font-mono break-all bg-gray-100 dark:bg-gray-800 p-2 rounded">
                {url || 'No URL provided'}
              </p>
            </div>
          ) : (
            <>
              {/* URL Preview Card */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4 border dark:border-gray-700">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                      {hostname}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 break-all line-clamp-2">
                      {url}
                    </p>
                  </div>
                </div>
              </div>

              {/* Security Notice */}
              <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg mb-4 border border-amber-200 dark:border-amber-800">
                <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-amber-800 dark:text-amber-200 font-medium">
                    External Content
                  </p>
                  <p className="text-amber-700 dark:text-amber-300 text-xs mt-0.5">
                    This link leads to an external website. Make sure you trust the source before visiting.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={handleOpenExternal} 
                  className="flex-1 gap-2 bg-blue-600 hover:bg-blue-700"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open in New Tab
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCopyUrl}
                  className="flex-1 gap-2"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 text-green-600" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy Link
                    </>
                  )}
                </Button>
              </div>

              {/* Optional: Try Embedded View */}
              {!showIframe && !iframeBlocked && (
                <div className="mt-4 pt-4 border-t dark:border-gray-700">
                  <Button
                    variant="ghost"
                    onClick={handleTryIframe}
                    className="w-full text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-sm"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try embedded preview (may not work for all sites)
                  </Button>
                </div>
              )}

              {/* Embedded iframe (when requested) */}
              {showIframe && !iframeBlocked && (
                <div className="mt-4 pt-4 border-t dark:border-gray-700">
                  <div className="relative h-[400px] bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
                    {isLoading && (
                      <div className="absolute inset-0 flex items-center justify-center z-10">
                        <div className="text-center">
                          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-3" />
                          <p className="text-gray-600 dark:text-gray-400 text-sm">Loading preview...</p>
                        </div>
                      </div>
                    )}
                    <iframe
                      key={url}
                      src={url}
                      title="External Content Preview"
                      className="w-full h-full border-0"
                      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation"
                      referrerPolicy="no-referrer"
                      onLoad={handleIframeLoad}
                      onError={handleIframeError}
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                    Content is sandboxed for your protection
                  </p>
                </div>
              )}

              {/* Iframe blocked message */}
              {iframeBlocked && showIframe && (
                <div className="mt-4 pt-4 border-t dark:border-gray-700">
                  <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 text-center">
                    <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      This website cannot be previewed here due to its security settings.
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      Please use "Open in New Tab" to view this content.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-t rounded-b-lg">
          <div className="flex items-center justify-end">
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default URLViewerModal;
